import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  CheckCircle2,
  Loader2,
  ArrowRight,
  Shield,
  Wrench,
  Package,
  BarChart3,
  Award,
  ClipboardCheck
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { trackEvent, EVENTS, setUserProperty } from '../lib/analytics';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  { icon: Package, text: 'Track assets, components & lifting gear in one register' },
  { icon: Wrench, text: 'Create work orders with task checklists and time tracking' },
  { icon: ClipboardCheck, text: 'Run inspections with mobile-friendly checklists' },
  { icon: Award, text: 'Manage certificates with auto-expiry alerts' },
  { icon: Shield, text: 'Compliance standards library (AS 2550, AS 1418, etc.)' },
  { icon: BarChart3, text: 'Insights dashboard with cost tracking and analytics' },
];

const COUNTRIES = [
  'Australia', 'New Zealand', 'United Kingdom', 'United States', 'Canada',
  'South Africa', 'United Arab Emirates', 'Singapore', 'India', 'Other'
];

const TestDrivePage = () => {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    company_name: '',
    fleet_size: '',
    country: '',
  });
  const [agreed, setAgreed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.company_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!agreed) {
      toast.error('Please agree to the Privacy Policy');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/test-drive/signup`, form);
      const { token, user, client_id, password } = res.data;

      // Push the new session into AuthContext so ProtectedRoute sees us as
      // authenticated on the very next render (writing to localStorage alone
      // is not enough — AuthContext state was captured on mount).
      setSession(token, user);

      // GA conversion event — primary acquisition signal
      setUserProperty('plan', 'free');
      trackEvent(EVENTS.TEST_DRIVE_SIGNUP, {
        method: 'frictionless_form',
        fleet_size: form.fleet_size || 'unspecified',
        country: form.country || 'unspecified',
      });
      // Also fire the GA4-recommended sign_up so it lights up the standard funnel
      trackEvent(EVENTS.SIGN_UP, { method: 'test_drive' });

      toast.success(`Welcome to Guardian! Your Client ID is ${client_id}`);

      // Send the new admin to the onboarding screen (NOT straight to dashboard)
      setTimeout(() => navigate('/onboarding', { replace: true }), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#121212] flex" data-testid="test-drive-page">

      {/* Left — Marketing */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center px-16 xl:px-24">
        <div className="mb-8">
          <h1 className="font-heading text-4xl xl:text-5xl font-bold text-white leading-tight">
            Ready to explore<br />
            <span className="text-[#FFD108]">EQUIPR Guardian</span>?
          </h1>
          <p className="text-[hsl(0,0%,63%)] mt-4 text-lg max-w-md">
            The maintenance management platform built for heavy equipment, cranes, and lifting operations.
          </p>
        </div>

        <div className="space-y-4">
          {FEATURES.map((feat, i) => {
            const Icon = feat.icon;
            return (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#FFD108]/10 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-[#FFD108]" />
                </div>
                <p className="text-white text-sm">{feat.text}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-12 pt-8 border-t border-[#2E2E2E]">
          <p className="text-[hsl(0,0%,63%)] text-sm">
            Start and stay free — up to 10 assets. Upgrade when you're ready.<br />
            <span className="text-[#FFD108]">$39/office user/month</span> for Professional. Field users always free.
          </p>
        </div>
      </div>

      {/* Right — Signup Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-16 bg-[#1C1C1C] lg:bg-[#FAFAFA]">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <h1 className="font-heading text-2xl font-bold text-white">
              EQUIPR <span className="text-[#FFD108]">Guardian</span>
            </h1>
          </div>

          <Card className="lg:bg-white lg:border-gray-200 bg-[#262626] border-[#2E2E2E]">
            <CardContent className="p-8">
              <h2 className="font-heading text-xl font-bold lg:text-gray-900 text-white mb-1">
                Get started for free
              </h2>
              <p className="text-sm lg:text-gray-500 text-[hsl(0,0%,63%)] mb-6">
                We'll set up a workspace with sample data so you can explore instantly.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium lg:text-gray-700 text-gray-400">First name *</Label>
                    <Input
                      value={form.first_name}
                      onChange={e => setForm({ ...form, first_name: e.target.value })}
                      placeholder="John"
                      className="mt-1 lg:bg-white lg:border-gray-300 lg:text-gray-900 bg-[#1C1C1C] border-[#2E2E2E] text-white"
                      data-testid="td-first-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium lg:text-gray-700 text-gray-400">Last name</Label>
                    <Input
                      value={form.last_name}
                      onChange={e => setForm({ ...form, last_name: e.target.value })}
                      placeholder="Smith"
                      className="mt-1 lg:bg-white lg:border-gray-300 lg:text-gray-900 bg-[#1C1C1C] border-[#2E2E2E] text-white"
                      data-testid="td-last-name"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium lg:text-gray-700 text-gray-400">Work email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="john@company.com"
                    className="mt-1 lg:bg-white lg:border-gray-300 lg:text-gray-900 bg-[#1C1C1C] border-[#2E2E2E] text-white"
                    data-testid="td-email"
                  />
                </div>

                <div>
                  <Label className="text-xs font-medium lg:text-gray-700 text-gray-400">Company name *</Label>
                  <Input
                    value={form.company_name}
                    onChange={e => setForm({ ...form, company_name: e.target.value })}
                    placeholder="Your company"
                    className="mt-1 lg:bg-white lg:border-gray-300 lg:text-gray-900 bg-[#1C1C1C] border-[#2E2E2E] text-white"
                    data-testid="td-company"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium lg:text-gray-700 text-gray-400">Fleet size</Label>
                    <Select value={form.fleet_size} onValueChange={v => setForm({ ...form, fleet_size: v })}>
                      <SelectTrigger className="mt-1 lg:bg-white lg:border-gray-300 lg:text-gray-900 bg-[#1C1C1C] border-[#2E2E2E] text-white" data-testid="td-fleet-size">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-10">1 – 10</SelectItem>
                        <SelectItem value="11-50">11 – 50</SelectItem>
                        <SelectItem value="51-200">51 – 200</SelectItem>
                        <SelectItem value="200+">200+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium lg:text-gray-700 text-gray-400">Country</Label>
                    <Select value={form.country} onValueChange={v => setForm({ ...form, country: v })}>
                      <SelectTrigger className="mt-1 lg:bg-white lg:border-gray-300 lg:text-gray-900 bg-[#1C1C1C] border-[#2E2E2E] text-white" data-testid="td-country">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Agreement */}
                <div className="flex items-start gap-2 pt-1">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={e => setAgreed(e.target.checked)}
                    className="mt-1 rounded"
                    data-testid="td-agree-checkbox"
                  />
                  <p className="text-xs lg:text-gray-500 text-[hsl(0,0%,63%)]">
                    By starting, you agree to EQUIPR's{' '}
                    <a href="https://www.equiprsoftware.com/legal/" target="_blank" rel="noopener noreferrer" className="text-[#FFD108] hover:underline" data-testid="td-privacy-link">Privacy Policy</a>{' '}
                    and{' '}
                    <a href="https://www.equiprsoftware.com/legal/" target="_blank" rel="noopener noreferrer" className="text-[#FFD108] hover:underline" data-testid="td-terms-link">Terms of Service</a>.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 btn-volt text-base"
                  data-testid="td-submit-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Get started for free
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              <p className="text-center text-xs lg:text-gray-400 text-[hsl(0,0%,63%)] mt-4">
                Already have an account?{' '}
                <a href="/login" className="text-[#FFD108] hover:underline">Log in</a>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TestDrivePage;

